import { useEffect, useRef, useState, type RefObject } from 'react'
import { KeyRound, LogIn, Mail } from 'lucide-react'
import { Button, ButtonLink, Form, Notice, TextField } from '@/components/play'
import { oauthStartUrl } from '@/components/onboarding/authContinuation'
import { waitWords } from '@/lib/retryWait'
import { apiFailure } from '@/lib/apiFailure'
import { clearIntent, saveIntent, type WatchIntent } from '@/lib/watch/intent'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import { WatchSheet } from './WatchSheet'

export type SignInSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * What the viewer was doing. It names the sheet and is completed back on this screen after signing in. Null for the
   * header's Sign in: the sheet is titled Sign in and nothing completes afterwards.
   */
  intent: WatchIntent | null
  /** Opens on the email field, for someone who needs an emailed link in particular (to confirm their address). */
  emailFirst?: boolean
}

const TITLES: Record<WatchIntent['kind'], string> = {
  chat: 'Sign in to chat',
  go_live: 'Sign in to go live',
  remind: 'Sign in for a reminder',
}

const LINK_FAILED = 'We couldn’t send a link. Try again.'
const LINK_OFFLINE = 'We couldn’t send a link. Check your connection and try again.'
const TOO_MANY = 'Too many requests. Try again shortly.'
const SIGN_IN_FAILED = 'We couldn’t sign you in. Try again.'
const SIGN_IN_OFFLINE = 'We couldn’t sign you in. Check your connection and try again.'

/**
 * Sign-in at intent on the watch screen (docs/features/watch-experience.md): Google, Strava, an emailed link, or the
 * account's password (superadmins have no emailed link), each returning to this screen as a viewer. The intent is saved
 * just before leaving for a provider (marked as this tab's, so coming back signed out abandons it), once the link is on
 * its way, or just before the password is checked; the page completes it after sign-in. Without an intent an older
 * saved one is forgotten at those moments instead, so it can't complete after this sign-in.
 */
export function SignInSheet({ open, onOpenChange, intent, emailFirst = false }: SignInSheetProps) {
  const emailInput = useRef<HTMLInputElement>(null)
  return (
    <WatchSheet
      open={open}
      onOpenChange={onOpenChange}
      title={intent ? TITLES[intent.kind] : 'Sign in'}
      initialFocus={emailFirst ? emailInput : undefined}
    >
      <SignInChoices intent={intent} emailInput={emailInput} onSignedIn={() => onOpenChange(false)} />
    </WatchSheet>
  )
}

function SignInChoices({ intent, emailInput, onSignedIn }: { intent: WatchIntent | null; emailInput: RefObject<HTMLInputElement>; onSignedIn: () => void }) {
  const { login } = useAuth()
  const returnTo = `${window.location.pathname}${window.location.search}`
  /**
   * Saves the intent at the moment the viewer leaves (or the link is on its way). Without one, `clearIntent()` empties
   * the saved slot on purpose. It is a single `localStorage` entry shared by every tab and event, so this may drop an
   * intent another tab saved (a chat waiting for its emailed link, say). That is the trade-off: kept, it would complete
   * here after this sign-in, sending a message or opening a camera check the viewer didn't ask for in this tab. What
   * the other tab's viewer typed isn't lost: it stays in that tab's chat field (the draft is kept per tab), only the
   * automatic send after signing in goes.
   */
  const remember = (leaving: boolean) => (intent ? saveIntent(intent, { leaving }) : clearIntent())
  const [email, setEmail] = useState('')
  // A password typed turns the form's one action from emailing a link into signing in with it.
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  // The address a link was asked for. The API answers the same for every address, so this says where the link went,
  // never that an account exists.
  const [sentTo, setSentTo] = useState<string | null>(null)
  const sentNotice = useRef<HTMLDivElement>(null)
  const returningToForm = useRef(false)

  useEffect(() => {
    if (sentTo) {
      sentNotice.current?.focus()
    } else if (returningToForm.current) {
      returningToForm.current = false
      emailInput.current?.focus()
      emailInput.current?.select()
    }
  }, [sentTo])

  async function signIn() {
    const address = email.trim()
    if (sending || !address) return
    setError(null)
    setEmailError(null)
    setSending(true)
    // Saved first: signing in reloads the stage, and the page completes the intent once the stage speaks for the account.
    remember(false)
    try {
      await login(address, password)
      onSignedIn()
    } catch (failure) {
      clearIntent()
      const { status, message, retryAfterMs } = apiFailure(failure)
      if (status === null) setError(SIGN_IN_OFFLINE)
      else if (status === 429) {
        const wait = waitWords(retryAfterMs)
        setError(wait ? `Too many sign-in attempts. Try again in ${wait}.` : message || TOO_MANY)
      } else setError(message || SIGN_IN_FAILED)
    } finally {
      setSending(false)
    }
  }

  async function sendLink() {
    const address = email.trim()
    if (sending || !address) return
    setError(null)
    setEmailError(null)
    setSending(true)
    try {
      await api.post('/auth/magic_link', { email: address, return_to: returnTo })
      remember(false)
      setSentTo(address)
    } catch (failure) {
      const { status, message, retryAfterMs } = apiFailure(failure)
      if (status === null) setError(LINK_OFFLINE)
      else if (status === 422) setEmailError(message || 'Enter a valid email address.')
      else if (status === 429) {
        const wait = waitWords(retryAfterMs)
        setError(wait ? `Too many link requests. Try again in ${wait}.` : message || TOO_MANY)
      } else setError(LINK_FAILED)
    } finally {
      setSending(false)
    }
  }

  if (sentTo) {
    return (
      <div className="watch-sign-in">
        <Notice ref={sentNotice} tabIndex={-1} tone="success" title="Check your email" className="watch-sign-in__sent">
          A sign-in link is on its way to <strong>{sentTo}</strong>.
        </Notice>
        <Button
          variant="secondary"
          onClick={() => {
            returningToForm.current = true
            setSentTo(null)
          }}
        >
          Use a different email
        </Button>
      </div>
    )
  }

  return (
    <div className="watch-sign-in">
      <div className="watch-sign-in__providers">
        <ButtonLink variant="secondary" href={oauthStartUrl('google', returnTo, 'viewer')} onClick={() => remember(true)}>
          <GoogleMark />
          Continue with Google
        </ButtonLink>
        <ButtonLink variant="secondary" href={oauthStartUrl('strava', returnTo, 'viewer')} onClick={() => remember(true)}>
          <StravaMark />
          Continue with Strava
        </ButtonLink>
      </div>
      <p className="watch-sign-in__or">or</p>
      <Form className="watch-sign-in__email" onFormSubmit={() => void (password ? signIn() : sendLink())}>
        {error && <Notice tone="error" alert>{error}</Notice>}
        <TextField
          ref={emailInput}
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          enterKeyHint="send"
          placeholder="you@example.com"
          icon={<Mail />}
          required
          requiredMessage="Enter your email address."
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            setEmailError(null)
          }}
          error={emailError ?? undefined}
        />
        <TextField
          name="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          icon={<KeyRound />}
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
            setError(null)
          }}
        />
        <Button type="submit" loading={sending}>
          {password ? <LogIn aria-hidden="true" /> : <Mail aria-hidden="true" />}
          {password ? 'Sign in' : 'Email me a link'}
        </Button>
      </Form>
    </div>
  )
}

// Brand artwork, not icons: the providers' marks keep their own colours, as their sign-in branding asks. Google's "G" is
// its official mark (Google's 48dp logo_googleg, gstatic), Strava's the prototype's.

function GoogleMark() {
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M117.6 61.364c0-4.255-.382-8.346-1.091-12.273H60V72.3h32.291c-1.391 7.5-5.618 13.855-11.973 18.11v15.054h19.391c11.346-10.446 17.891-25.828 17.891-44.1z" />
      <path fill="#34A853" d="M60 120c16.2 0 29.782-5.373 39.709-14.536L80.318 90.41C74.945 94.009 68.073 96.136 60 96.136c-15.627 0-28.855-10.554-33.573-24.736H6.382v15.545C16.255 106.555 36.545 120 60 120z" />
      <path fill="#FBBC05" d="M26.427 71.4c-1.2-3.6-1.882-7.445-1.882-11.4s.682-7.8 1.882-11.4V33.055H6.382C2.318 41.155 0 50.318 0 60s2.318 18.845 6.382 26.945L26.427 71.4z" />
      <path fill="#EA4335" d="M60 23.864c8.809 0 16.718 3.027 22.936 8.972l17.21-17.209C89.754 5.945 76.172 0 60 0 36.545 0 16.255 13.445 6.382 33.055L26.427 48.6C31.145 34.418 44.373 23.864 60 23.864z" />
    </svg>
  )
}

function StravaMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#FC4C02" d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
  )
}
